export type ECPngExportOptions = {
  pixelRatio?: number;
  signal?: AbortSignal;
};

export type ECPngExportPage = {
  blob: Blob;
  width: number;
  height: number;
};

export type ECPngExportResult = {
  pages: readonly ECPngExportPage[];
};

export type ECPngRenderRequest = {
  pixelRatio: number;
  signal: AbortSignal;
};

export type ECPngRenderer = (request: ECPngRenderRequest) => Promise<readonly HTMLCanvasElement[]>;

export enum ECPngExportErrorCode {
  InvalidPixelRatio = "invalidPixelRatio",
  Unavailable = "unavailable",
  Busy = "busy",
  Aborted = "aborted",
  Timeout = "timeout",
  InvalidPages = "invalidPages",
  EncodingFailed = "encodingFailed",
}

export class ECPngExportError extends Error {
  readonly code: ECPngExportErrorCode;

  constructor(code: ECPngExportErrorCode, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "ECPngExportError";
    this.code = code;
  }
}

type ECPngRendererRegistration = {
  acquisition: symbol;
  renderer: ECPngRenderer;
  lifetimeController: AbortController;
};

const DEFAULT_PIXEL_RATIO = 2;
const MAX_PIXEL_RATIO = 2;
const EXPORT_TIMEOUT_MS = 30_000;
const rendererRegistrations = new Map<string, ECPngRendererRegistration>();
const busyEcIds = new Set<string>();

function createAbortError(ecId: string, cause?: unknown): ECPngExportError {
  return new ECPngExportError(
    ECPngExportErrorCode.Aborted,
    `PNG export for EC "${ecId}" was aborted.`,
    cause === undefined ? undefined : { cause }
  );
}

function getSignalError(ecId: string, signal: AbortSignal): ECPngExportError {
  return signal.reason instanceof ECPngExportError
    ? signal.reason
    : createAbortError(ecId, signal.reason);
}

function createExportSignal(
  ecId: string,
  lifetimeSignal: AbortSignal,
  callerSignal?: AbortSignal
): { signal: AbortSignal; cleanup: VoidFunction } {
  const controller = new AbortController();
  const cleanups: VoidFunction[] = [];

  for (const signal of [lifetimeSignal, callerSignal]) {
    if (signal === undefined) {
      continue;
    }

    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }

    const onAbort = () => {
      if (!controller.signal.aborted) {
        controller.abort(signal.reason);
      }
    };
    signal.addEventListener("abort", onAbort, { once: true });
    cleanups.push(() => {
      signal.removeEventListener("abort", onAbort);
    });
  }

  const timeout = controller.signal.aborted
    ? undefined
    : setTimeout(() => {
        controller.abort(
          new ECPngExportError(
            ECPngExportErrorCode.Timeout,
            `PNG export for EC "${ecId}" timed out after ${EXPORT_TIMEOUT_MS}ms.`
          )
        );
      }, EXPORT_TIMEOUT_MS);

  return {
    signal: controller.signal,
    cleanup: () => {
      if (timeout !== undefined) {
        clearTimeout(timeout);
      }
      for (const cleanup of cleanups) {
        cleanup();
      }
    },
  };
}

function waitForAbortable<T>(promise: Promise<T>, signal: AbortSignal, ecId: string): Promise<T> {
  if (signal.aborted) {
    return Promise.reject(getSignalError(ecId, signal));
  }

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      reject(getSignalError(ecId, signal));
    };
    const cleanup = () => {
      signal.removeEventListener("abort", onAbort);
    };

    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        cleanup();
        resolve(value);
      },
      (error: unknown) => {
        cleanup();
        reject(error);
      }
    );
  });
}

function isValidCanvas(value: unknown): value is HTMLCanvasElement {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const canvas = value as Partial<HTMLCanvasElement>;
  return (
    Number.isSafeInteger(canvas.width) &&
    Number(canvas.width) > 0 &&
    Number.isSafeInteger(canvas.height) &&
    Number(canvas.height) > 0 &&
    typeof canvas.toBlob === "function"
  );
}

function resetRenderedCanvases(value: unknown): void {
  if (!Array.isArray(value)) {
    return;
  }

  for (const canvas of value) {
    if (typeof canvas === "object" && canvas !== null && "width" in canvas && "height" in canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}

function encodeCanvas(canvas: HTMLCanvasElement, signal: AbortSignal, ecId: string): Promise<Blob> {
  if (signal.aborted) {
    return Promise.reject(getSignalError(ecId, signal));
  }

  const encoded = new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob === null) {
        reject(
          new ECPngExportError(
            ECPngExportErrorCode.EncodingFailed,
            `A PNG page for EC "${ecId}" could not be encoded.`
          )
        );
        return;
      }
      resolve(blob);
    }, "image/png");
  });

  return waitForAbortable(encoded, signal, ecId).catch((error: unknown) => {
    if (error instanceof ECPngExportError) {
      throw error;
    }
    throw new ECPngExportError(
      ECPngExportErrorCode.EncodingFailed,
      `A PNG page for EC "${ecId}" could not be encoded.`,
      { cause: error }
    );
  });
}

function encodeCanvases(
  canvases: readonly HTMLCanvasElement[],
  signal: AbortSignal,
  ecId: string
): Promise<readonly ECPngExportPage[]> {
  return canvases.reduce<Promise<ECPngExportPage[]>>(async (pagesPromise, canvas) => {
    const pages = await pagesPromise;
    const blob = await encodeCanvas(canvas, signal, ecId);
    pages.push({ blob, width: canvas.width, height: canvas.height });
    return pages;
  }, Promise.resolve([]));
}

export function acquireECPngRenderer(ecId: string, renderer: ECPngRenderer): VoidFunction {
  const previousRegistration = rendererRegistrations.get(ecId);
  const registration: ECPngRendererRegistration = {
    acquisition: Symbol("ec-png-renderer-acquisition"),
    renderer,
    lifetimeController: new AbortController(),
  };

  rendererRegistrations.set(ecId, registration);
  previousRegistration?.lifetimeController.abort(createAbortError(ecId));

  return () => {
    const currentRegistration = rendererRegistrations.get(ecId);
    if (currentRegistration?.acquisition !== registration.acquisition) {
      return;
    }

    rendererRegistrations.delete(ecId);
    registration.lifetimeController.abort(createAbortError(ecId));
  };
}

export async function exportECPng(
  ecId: string,
  options: ECPngExportOptions = {}
): Promise<ECPngExportResult> {
  const pixelRatio = options.pixelRatio ?? DEFAULT_PIXEL_RATIO;
  if (!Number.isFinite(pixelRatio) || pixelRatio <= 0 || pixelRatio > MAX_PIXEL_RATIO) {
    throw new ECPngExportError(
      ECPngExportErrorCode.InvalidPixelRatio,
      `PNG export pixelRatio must be finite, greater than 0, and at most ${MAX_PIXEL_RATIO}.`
    );
  }

  const registration = rendererRegistrations.get(ecId);
  if (registration === undefined) {
    throw new ECPngExportError(
      ECPngExportErrorCode.Unavailable,
      `No mounted PNG renderer is available for EC "${ecId}".`
    );
  }
  if (busyEcIds.has(ecId)) {
    throw new ECPngExportError(
      ECPngExportErrorCode.Busy,
      `A PNG export is already running for EC "${ecId}".`
    );
  }

  busyEcIds.add(ecId);
  const exportSignal = createExportSignal(
    ecId,
    registration.lifetimeController.signal,
    options.signal
  );
  let canvases: readonly HTMLCanvasElement[] | undefined;

  try {
    if (exportSignal.signal.aborted) {
      throw getSignalError(ecId, exportSignal.signal);
    }

    const renderPromise = Promise.resolve(
      registration.renderer({ pixelRatio, signal: exportSignal.signal })
    );
    try {
      canvases = await waitForAbortable(renderPromise, exportSignal.signal, ecId);
    } catch (error) {
      if (exportSignal.signal.aborted) {
        void renderPromise.then(resetRenderedCanvases, () => undefined);
      }
      throw error;
    }

    if (!Array.isArray(canvases) || canvases.length === 0 || !canvases.every(isValidCanvas)) {
      throw new ECPngExportError(
        ECPngExportErrorCode.InvalidPages,
        `PNG renderer for EC "${ecId}" returned no valid pages.`
      );
    }

    return {
      pages: await encodeCanvases(canvases, exportSignal.signal, ecId),
    };
  } finally {
    resetRenderedCanvases(canvases);
    exportSignal.cleanup();
    busyEcIds.delete(ecId);
  }
}
